import { prettyError, prettyErrorDescription } from 'constants/errors'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import formServices from 'services/form-builder-services'
import toast from 'react-hot-toast'
import textServices from 'services/text-services'
import { Label } from 'theme/theme'
import Input from "components/input";
import { usePopupContext } from 'hooks/use-main-cass'
import { useContext, useEffect } from 'react'
import { LoaderContext } from 'contexts/loader-context'
import ProfileService from 'services/profile-services'
import { useSession } from 'contexts/session-context'
import { dlTrackError } from 'components/dataLayer/dataLayer'
import { AxiosError } from 'axios'
import { useLocalStorage } from 'react-use'
import { AddressAdapter } from 'adapters/address-adapter'
import Radio from 'components/radio'
import EditAddressStep2 from './EditAddressStep2'

interface IProps {
  defaultValues: {
    street1?: string
    streetType?: string
    streetNumber?: string
    apartament?: string
    postalCode?: string
    neighborhood?: string
    city?: string
    state?: string
  }
}

const EditAddressV2 = ({ defaultValues }: IProps) => {
  const { closePopup, openPopup } = usePopupContext()
  const { setLoad } = useContext(LoaderContext)
  const [state, setState] = useLocalStorage('state', {})
  const [{ data: session }, { setUser }] = useSession()
  const { register, handleSubmit, setValue, getValues, formState: { errors } } = useForm({
    resolver: yupResolver(formServices.formGenerateSchema(["street1", "streetType", "streetNumber", "apartament", "postalCode"])),
    criteriaMode: 'firstError',
    reValidateMode: 'onSubmit',
    mode: 'all'
  })

  useEffect(() => {
    if (defaultValues) {
      setValue('street1', defaultValues?.street1)
      setValue('streetType', defaultValues?.streetType)
      setValue('streetNumber', defaultValues?.streetNumber)
      setValue('apartament', defaultValues?.apartament)
      setValue('postalCode', defaultValues?.postalCode)
    }
  }, [defaultValues])

  const handleError = (error: AxiosError) => {
    if (error?.isAxiosError) {
      const { code: errorCode, message: errorMessage } = error?.response?.data ?? {}
      dlTrackError({
        error_type: 'login',
        error_code: { errorCode },
        error_message: { errorMessage },
        section: 'login'
      })
      const titleErrors = [59, 63]
      if (titleErrors.includes(errorCode)) {
        toast.error(prettyError(errorCode), { className: 'toast__error' })
      } else {
        toast.error(prettyErrorDescription(errorCode), { className: 'toast__error' })
      }
    }
  }

  const onSubmit = async (getValues2: () => any) => {
    setLoad(true)
    try {
      const step1 = getValues()
      const step2 = getValues2()
      const adaptedData = AddressAdapter({ ...step1, ...step2 })
      const response = await ProfileService.updateAddres({ address: adaptedData, session })
      setState({ ...state, user: response?.data })
      setUser(response?.data)
      toast.success('Los cambios se guardaron con éxito', { className: 'toast__success' })
      closePopup()
    } catch (err) {
      handleError(err)
    } finally {
      setLoad(false)
    }
  }

  async function onFormSubmit(data: any) {
    setLoad(true)
    try {
      const response = await ProfileService.checkZip({ postalCode: data.postalCode }, { session })
      const cityDefault = response.data[0].cities[0].name
      const stateDefault = response.data[0].name
      const city = response.data[0].cities[0]
      const state = response.data[0]
      const neighborhood = response.data[0].cities[0].neighborhoods

      openPopup(<EditAddressStep2 getValuesStep={getValues} city={city} state={state} neighborhood={neighborhood} defaultValues={{ city: cityDefault, state: stateDefault, neighborhood: defaultValues?.neighborhood }} onSubmit={onSubmit} />)
    } catch (err) {
      handleError(err)
    } finally {
      setLoad(false)
    }
  }

  const onInvalid = (errors: Record<string, any>) => {
    if (errors) {
      const errorsKeys = Object.values(errors)
      toast.error(errorsKeys[0]?.message, { className: 'toast__error' })
    }
  };

  return (
    <div className="pb-4">
      <h2 className="form__sign-in__title">Editar dirección</h2>
      <form onSubmit={handleSubmit(onFormSubmit, onInvalid)} className='space-y-4'>
        <div className={`gap-2 flex flex-col input-container ${errors?.street1?.message ? 'error-input' : ''}`}>
          <div className='flex items-center gap-2'>
            <svg width="9" height="13" viewBox="0 0 9 13" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 0.28125C5.26172 0.28125 5.96973 0.47168 6.62402 0.852539C7.25879 1.2334 7.7666 1.74121 8.14746 2.37598C8.52832 3.03027 8.71875 3.73828 8.71875 4.5C8.71875 4.80273 8.66016 5.1543 8.54297 5.55469C8.44531 5.88672 8.29395 6.28223 8.08887 6.74121C7.75684 7.49316 7.32227 8.33301 6.78516 9.26074C6.39453 9.94434 5.95996 10.6475 5.48145 11.3701L4.5 12.8203L4.11914 12.2637C3.96289 12.0391 3.7627 11.7412 3.51855 11.3701C3.04004 10.6475 2.60547 9.94434 2.21484 9.26074C1.67773 8.33301 1.24316 7.49316 0.911133 6.74121C0.706055 6.28223 0.554688 5.88672 0.457031 5.55469C0.339844 5.1543 0.28125 4.80273 0.28125 4.5C0.28125 3.73828 0.47168 3.03027 0.852539 2.37598C1.2334 1.74121 1.74121 1.2334 2.37598 0.852539C3.03027 0.47168 3.73828 0.28125 4.5 0.28125ZM4.5 1.21875C3.9043 1.21875 3.35254 1.36523 2.84473 1.6582C2.34668 1.95117 1.95117 2.35156 1.6582 2.85938C1.36523 3.35742 1.21875 3.9043 1.21875 4.5C1.21875 4.68555 1.26758 4.94922 1.36523 5.29102C1.46289 5.62305 1.59473 5.97949 1.76074 6.36035C2.04395 7.00488 2.46875 7.81543 3.03516 8.79199C3.51367 9.6123 3.99219 10.3838 4.4707 11.1064L4.5 11.1357L4.5293 11.1064C5.00781 10.3838 5.48633 9.6123 5.96484 8.79199C6.53125 7.81543 6.95605 7.00488 7.23926 6.36035C7.40527 5.97949 7.53711 5.62305 7.63477 5.29102C7.73242 4.94922 7.78125 4.68555 7.78125 4.5C7.78125 3.9043 7.63477 3.35742 7.3418 2.85938C7.04883 2.35156 6.64844 1.95117 6.14062 1.6582C5.64258 1.36523 5.0957 1.21875 4.5 1.21875ZM4.5 3.5625C4.76367 3.5625 4.9834 3.65527 5.15918 3.84082C5.34473 4.0166 5.4375 4.23633 5.4375 4.5C5.4375 4.76367 5.34473 4.98828 5.15918 5.17383C4.9834 5.34961 4.76367 5.4375 4.5 5.4375C4.23633 5.4375 4.01172 5.34961 3.82617 5.17383C3.65039 4.98828 3.5625 4.76367 3.5625 4.5C3.5625 4.23633 3.65039 4.0166 3.82617 3.84082C4.01172 3.65527 4.23633 3.5625 4.5 3.5625Z" fill="white" />
            </svg>
            <Label className="text-start text-2xl font-bold uppercase input-label">
              {textServices.findByText('profile/form/information/address/label', { es: '¿Tu dirección?', en: 'Your address?' })}
            </Label>
          </div>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder="Nombre de la calle"
            name="street1"
            {...register("street1")}
          />
        </div>
        <div className="flex items-center justify-between gap-3 input-container">
          <Radio type="radio" value="1" {...register("streetType")}>
            {textServices.findByText('profile/form/information/street/label', { es: 'Calle', en: 'Street' })}
          </Radio>
          <Radio type="radio" value="2" {...register("streetType")}>
            {textServices.findByText('profile/form/information/avenue/label', { es: 'Avenida', en: 'Avenue' })}
          </Radio>
          <Radio type="radio" value="3" {...register("streetType")}>
            {textServices.findByText('profile/form/information/closed/label', { es: 'Cerrada', en: 'Closed' })}
          </Radio>
        </div>
        <div className="flex flex-wrap items-center justify-between">
          <div className={`gap-2 flex flex-col input-container ${errors?.streetNumber?.message ? 'error-input' : ''}`} style={{ width: '48%' }}>
            <Input
              className="text-center saira font-bold text-sm "
              type="text"
              placeholder={textServices.findByText('profile/form/information/streetNumber/placeholder', { es: "Número", en: 'Number' })}
              name="streetNumber"
              {...register("streetNumber")}
            />
          </div>
          <div className={`gap-2 flex flex-col input-container ${errors?.apartament?.message ? 'error-input' : ''}`} style={{ width: '48%' }}>
            <Input
              className="text-center saira font-bold text-sm"
              type="text"
              placeholder={textServices.findByText('profile/form/information/apartment/placeholder', { es: "Apartamento", en: 'Apartment' })}
              name="apartament"
              {...register("apartament")}
            />
          </div>
        </div>
        <div className={`gap-2 flex flex-col input-container ${errors?.postalCode?.message ? 'error-input' : ''}`}>
          <Input
            className="text-center saira font-bold text-sm"
            type="text"
            placeholder={textServices.findByText('profile/form/information/postalCode/placeholder', { es: "CP - Código postal", en: 'CP - Postal code' })}
            name="postalCode"
            {...register("postalCode")}
          />
        </div>
        <div className='space-y-2'>
          <button type="submit" className="form__sign-in__btn form__sign-in__btn__main">Siguientes</button>
        </div>
      </form>
    </div>
  )
}

export default EditAddressV2
